import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import VoiceOnboarding from '../components/Onboarding/VoiceOnboarding';
import ProfileReview from '../components/Onboarding/ProfileReview';

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontSize: '14px',
  },
  noSession: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    gap: '16px',
  },
  noSessionText: {
    color: 'var(--text-secondary)',
    fontSize: '15px',
    maxWidth: '300px',
    lineHeight: 1.5,
  },
};

export default function VoiceOnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [resumeSessionId, setResumeSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatedProfile, setGeneratedProfile] = useState(null);

  // Fetch resumable session on mount
  useEffect(() => {
    const fetchResumable = async () => {
      try {
        const res = await api.get('/onboarding/resumable');
        if (res.data.session) {
          setResumeSessionId(res.data.session.id);
        }
      } catch {
        // no resumable session
      } finally {
        setLoading(false);
      }
    };
    fetchResumable();
  }, []);

  const handleVoiceComplete = useCallback((profile) => {
    setGeneratedProfile(profile);
  }, []);

  const handlePause = useCallback(async () => {
    await refreshUser();
    navigate('/discover');
  }, [refreshUser, navigate]);

  const handleProfileSaved = useCallback(async () => {
    await refreshUser();
    navigate('/profile');
  }, [refreshUser, navigate]);

  const handleRedoConversation = useCallback(() => {
    setGeneratedProfile(null);
    setResumeSessionId(null);
  }, []);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div className="spinner" />
      </div>
    );
  }

  // If profile was generated, show review screen
  if (generatedProfile) {
    return (
      <div style={styles.container}>
        <ProfileReview
          profile={generatedProfile}
          onSave={handleProfileSaved}
          onBack={handleRedoConversation}
        />
      </div>
    );
  }

  // No resumable session and user already completed onboarding
  if (!resumeSessionId && user?.onboarding_complete) {
    return (
      <div style={styles.noSession}>
        <p style={styles.noSessionText}>
          Your profile is already complete! You can edit it from your profile page.
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/profile')}>
          Go to Profile
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <VoiceOnboarding
        onComplete={handleVoiceComplete}
        onPause={handlePause}
        userName={user?.display_name}
        resumeSessionId={resumeSessionId}
      />
    </div>
  );
}
